// @composables/useActionToaster.ts
import { shallowRef } from 'vue';
import { ToastType } from 'flowbite-vue/dist/components/Toast/types';
import { useToasterStore } from '~/components/notifications/uhToasts/uhToastProvider/store';
import UhActionToast from '~/components/notifications/uhToasts/UhActionToast.vue';

export interface ToastAction {
  label: string;
  icon?: string[];
  onClick: () => void;
  class?: string;
}

export type UseActionToaster = {
  add: (
    type: ToastType,
    message: string,
    actions?: ToastAction[],
    title?: string,
    time?: number,
    id?: string,
    additionalMessage?: string,
    onClose?: () => void,
  ) => string;
};

/**
 * Composable for creating toasts with action buttons
 */
export const useActionToaster = (): UseActionToaster => {
  const store = useToasterStore();

  /**
   * Add a toast with action buttons
   * @param type Toast type (success, danger, warning, info)
   * @param message Message to display
   * @param actions Array of action buttons with onClick handlers
   * @param title Optional toast title
   * @param time Duration to show the toast in milliseconds
   * @param id Optional ID for the toast
   * @param additionalMessage Another line under the original message if we want spacing/split
   * @param onClose Optional callback when the toast is closed
   * @returns The ID of the created toast
   */
  const add = (
    type: ToastType,
    message: string,
    actions?: ToastAction[],
    title?: string,
    time?: number,
    id?: string,
    additionalMessage?: string,
    onClose?: () => void,
  ): string => {
    const toastId =
      id ||
      parseInt((new Date().getTime() * Math.random()).toString()).toString();

    return store.addToast({
      id: toastId,
      type,
      text: '',
      time: time || 30 * 1000,
      component: shallowRef(UhActionToast),
      componentProps: {
        type,
        title,
        message,
        actions,
        closable: true,
        divide: type !== 'empty',
        'data-toast-type': type,
        additionalMessage,
        onClose,
      },
    });
  };

  return {
    add,
  };
};
